/**
 * version.ts —— Round25 · M7 部署可达性：构建版本信息的单一真源。
 *
 * `__APP_COMMIT__ / __APP_VERSION__ / __BUILD_TIME__` 由 `vite.config.ts` 的 `define`
 * 在构建期内联成字面量（见该文件顶部注释），因此：
 *   - 运行时页面可读（`window.__APP_VERSION__`、调试信息条、设置页「关于」）；
 *   - 生产产物里能被 `verify:prod-clean` grep 到，用于发布门禁核验「部署产物 == HEAD」（AC-M7-2）。
 *
 * 这些常量在任何构建（dev / prod / diag）里都存在——它们**不是**调试能力，不受 DEBUG_TOOLS 门控，
 * 也**不属于**生产包禁词（`prod-bundle-scan` 白名单允许 `__APP_*`）。
 */

// 编译期常量（无 git 环境回退 'nogit'）。类型见 src/vite-env.d.ts。
export const APP_COMMIT: string = typeof __APP_COMMIT__ !== 'undefined' ? __APP_COMMIT__ : 'dev';
export const APP_VERSION: string = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
export const BUILD_TIME: string = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';

/** 供 UI 展示 / 日志的紧凑版本串，如 `v0.1.0 · 1a2b3c4d`。 */
export const VERSION_LABEL = `v${APP_VERSION} · ${APP_COMMIT}`;

/**
 * Round25 · M7.2 语义标志串（发布门禁核验「部署产物 == HEAD」）。
 *
 * 生产构建会 strip 掉注释，因此不能靠注释里的「全下优先/护栏/反加/开池加注」做门禁探针。
 * 这里用**运行时被引用的字符串常量**承载语义标志，minify 后仍保留在 bundle 里，
 * `verify:round25` 扫描产物 grep 这些串命中即证明「护栏/映射/口径」等本轮逻辑确实进了产物
 * （破除根因「真机跑的是没护栏的旧包」）。列表内容 = 各模块落地后的稳定行为指纹。
 */
export const BUILD_FLAGS: readonly string[] = [
  'round25-allin-priority', // M5：全下优先映射（player.allIn 覆盖 call/raise）
  'round25-preflop-guard', // M6：preflop all-in 护栏
  'round25-street-contributed', // M4：下注口径 = 本街累计 streetContributed
  'round25-pill-must-visible', // M2：行动 pill 硬可见（禁止合规隐藏）
  'round25-settle-body-min', // M2：结算正文最小可读高度
  'round25-folded-no-opacity', // M3：弃牌者去饱和不整卡半透明
  'round25-seat-angles', // M1：座位椭圆边缘确定性均布
];

export interface AppVersionInfo {
  version: string;
  commit: string;
  buildTime: string;
  label: string;
}

export const APP_VERSION_INFO: AppVersionInfo = {
  version: APP_VERSION,
  commit: APP_COMMIT,
  buildTime: BUILD_TIME,
  label: VERSION_LABEL,
};

/**
 * 把版本信息挂到全局，供真机「随手可查」与 QA / 自动化断言：
 *   - `window.__APP_VERSION__`（对象）：{ version, commit, buildTime, label }
 *   - `<meta name="app-commit">` / `<html data-app-commit>`：无需 JS 也能读到部署 commit。
 * 幂等，任何构建都会执行（不含任何调试能力）。
 */
export function exposeAppVersion(): void {
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).__APP_VERSION__ = APP_VERSION_INFO;
    // 引用 BUILD_FLAGS，使语义标志串被打进 bundle（不被 tree-shake），供发布门禁 grep（AC-M7-2）。
    (window as unknown as Record<string, unknown>).__APP_FLAGS__ = BUILD_FLAGS.join(',');
  }
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    root.setAttribute('data-app-commit', APP_COMMIT);
    root.setAttribute('data-app-version', APP_VERSION);
    let meta = document.head?.querySelector('meta[name="app-commit"]') as HTMLMetaElement | null;
    if (!meta && document.head) {
      meta = document.createElement('meta');
      meta.name = 'app-commit';
      document.head.appendChild(meta);
    }
    if (meta) meta.content = APP_COMMIT;
  }
}
